import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import CommonActionMenu from "../../components/common/CommonActionMenu";
import CommonFormActions from "../../components/common/CommonFormActions";
import CommonModal from "../../components/common/CommonModal";
import CommonTable from "../../components/common/CommonTable";
import MainLayout from "../../components/layout/MainLayout";
import api from "../../services/api";
import "./Holidays.css";

function AdHocHolidays() {
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [meta, setMeta] = useState({ departments: [], employees: [], teams: [] });
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    title: "",
    holiday_date: "",
    reason: "",
    scope_type: "company",
    target_ids: [],
  });

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const [metaResponse, holidaysResponse] = await Promise.all([
        api.get("/ad-hoc-holidays/meta"),
        api.get("/ad-hoc-holidays"),
      ]);

      setMeta(metaResponse.data?.data || { departments: [], employees: [], teams: [] });
      setHolidays(holidaysResponse.data?.data || []);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load holidays",
        text: error.response?.data?.message || "Please refresh and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const currentTargetOptions = useMemo(() => {
    if (form.scope_type === "department") {
      return (meta.departments || []).map((item) => ({ value: item.id, label: item.name }));
    }

    if (form.scope_type === "team") {
      return (meta.teams || []).map((item) => ({ value: item.id, label: item.name }));
    }

    if (form.scope_type === "employee") {
      return (meta.employees || []).map((item) => ({
        value: item.id,
        label: `${item.name} (${item.code})`,
      }));
    }

    return [];
  }, [form.scope_type, meta]);

  const resetForm = () => {
    setForm({
      title: "",
      holiday_date: "",
      reason: "",
      scope_type: "company",
      target_ids: [],
    });
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  const selectedTargetLabels = currentTargetOptions.filter((option) =>
    form.target_ids.includes(Number(option.value)),
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (form.scope_type !== "company" && form.target_ids.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Select at least one target",
        text: "Choose the department, team, or employee records that should receive this holiday.",
      });
      return;
    }

    try {
      const response = await api.post("/ad-hoc-holidays", form);

      Swal.fire({
        icon: "success",
        title: "Holiday saved",
        text: response.data?.message || "Ad hoc holiday saved successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      setShowModal(false);
      resetForm();
      fetchPageData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: error.response?.data?.message || "Unable to save ad hoc holiday.",
      });
    }
  };

  const handleDelete = async (holiday) => {
    const result = await Swal.fire({
      title: "Remove holiday?",
      text: `Delete ${holiday.title} on ${holiday.holiday_date}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6557ff",
      confirmButtonText: "Delete",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.delete(`/ad-hoc-holidays/${holiday.id}`);

      Swal.fire({
        icon: "success",
        title: "Holiday removed",
        text: response.data?.message || "Holiday removed successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      fetchPageData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: error.response?.data?.message || "Unable to delete this holiday.",
      });
    }
  };

  const columns = [
    { header: "Sr. No.", render: (_, index) => index + 1 },
    { header: "Title", render: (row) => row.title },
    { header: "Date", render: (row) => new Date(row.holiday_date).toLocaleDateString("en-IN") },
    {
      header: "Scope",
      render: (row) => (
        <span className={`scope-pill scope-${row.scope_type}`}>
          {row.scope_type.replace("_", " ")}
        </span>
      ),
    },
    { header: "Targets", render: (row) => row.scope_type === "company" ? "Entire company" : row.target_count || 0 },
    { header: "Reason", render: (row) => row.reason || "-" },
    { header: "Declared By", render: (row) => row.created_by_name || "N/A" },
    { header: "Declared At", render: (row) => row.created_at ? new Date(row.created_at).toLocaleString("en-IN") : "-" },
    {
      header: "Action",
      render: (row) => (
        <CommonActionMenu
          ariaLabel={`Actions for ${row.title}`}
          actions={[
            {
              label: "Delete",
              icon: "fas fa-trash",
              variant: "danger",
              onClick: () => handleDelete(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="weekly-holidays-container">
        <div className="holidays-card">
          <div className="card-header-custom">
            <div className="header-empty">
              <h3>Ad Hoc Holidays</h3>
              <p className="text-muted mb-0">Declare one-off holidays for the company, a department, a team, or specific employees.</p>
            </div>
            <button className="btn-add-holiday" onClick={handleOpenModal}>
              + Declare Holiday
            </button>
          </div>

          <div className="holiday-guidance-banner">
            <div>
              <span className="holiday-guidance-label">Payroll impact</span>
              <strong>These holidays flow into attendance and payroll calculations.</strong>
            </div>
            <p>Use company scope for universal holidays, or target departments, teams, and employees for one-off exceptions.</p>
          </div>

          <div className="card-body-custom">
            <CommonTable
              columns={columns}
              data={holidays}
              tableClassName="holidays-table-clean"
              noDataClassName="text-center py-5 no-data"
              emptyMessage={loading ? "Loading..." : "No ad hoc holidays declared"}
            />
          </div>
        </div>

        {showModal ? (
          <CommonModal
            onClose={() => setShowModal(false)}
            title="Declare Ad Hoc Holiday"
            overlayClassName="modal-backdrop-custom"
            contentClassName="modal-custom ad-hoc-holiday-modal"
            headerClassName="modal-header-custom"
            bodyClassName="modal-body-custom ad-hoc-holiday-modal-body"
            footerClassName="ad-hoc-holiday-modal-footer-shell"
            closeButtonClassName="close-modal-btn"
            footer={(
              <CommonFormActions
                className="ad-hoc-holiday-modal-footer"
                cancelLabel="Close"
                submitLabel="Save Holiday"
                onCancel={() => setShowModal(false)}
                onSubmit={handleSubmit}
                submitType="button"
              />
            )}
          >
            <form onSubmit={handleSubmit} className="ad-hoc-holiday-form">
              <div className="ad-hoc-holiday-row">
                <div className="ad-hoc-holiday-field">
                  <label>Holiday Title</label>
                  <input
                    type="text"
                    className="common-input"
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    required
                  />
                </div>
                <div className="ad-hoc-holiday-field">
                  <label>Date</label>
                  <input
                    type="date"
                    className="common-input"
                    value={form.holiday_date}
                    onChange={(event) => setForm((prev) => ({ ...prev, holiday_date: event.target.value }))}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
              </div>

              <div className="ad-hoc-holiday-row">
                <div className="ad-hoc-holiday-field">
                  <label>Scope</label>
                  <select
                    className="common-input"
                    value={form.scope_type}
                    onChange={(event) => setForm((prev) => ({ ...prev, scope_type: event.target.value, target_ids: [] }))}
                  >
                    <option value="company">Entire Company</option>
                    <option value="department">Department</option>
                    <option value="team">Team</option>
                    <option value="employee">Employee</option>
                  </select>
                </div>
                <div className="ad-hoc-holiday-field">
                  <label>Reason</label>
                  <input
                    type="text"
                    className="common-input"
                    value={form.reason}
                    onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
                  />
                </div>
              </div>

              {form.scope_type !== "company" ? (
                <div className="ad-hoc-holiday-target-block">
                  <div className="ad-hoc-holiday-field">
                    <label>Targets</label>
                    <p className="holiday-target-help">
                      Select one or more records. Hold Ctrl or Command to choose multiple options.
                    </p>
                    <select
                      multiple
                      className="common-input ad-hoc-holiday-target-select"
                      value={form.target_ids.map(String)}
                      onChange={(event) => {
                        const selected = Array.from(event.target.selectedOptions).map((option) => Number(option.value));
                        setForm((prev) => ({ ...prev, target_ids: selected }));
                      }}
                    >
                      {currentTargetOptions.map((option) => (
                        <option key={`${form.scope_type}-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="holiday-target-help">
                      {selectedTargetLabels.length > 0
                        ? `${selectedTargetLabels.length} target${selectedTargetLabels.length > 1 ? "s" : ""} selected`
                        : `No ${form.scope_type} selected yet`}
                    </p>
                    {selectedTargetLabels.length > 0 ? (
                      <div className="holiday-target-chips">
                        {selectedTargetLabels.map((option) => (
                          <span className="holiday-target-chip" key={`${form.scope_type}-chip-${option.value}`}>
                            {option.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </form>
          </CommonModal>
        ) : null}
      </div>
    </MainLayout>
  );
}

export default AdHocHolidays;
